import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, MessageSquare, ChevronLeft, Send, User, Clock, Check, X, AlertCircle } from "lucide-react";
import StatusBadge from "../components/StatusBadge";

const ticketsData = [
  { id: "req-1", requestId: "REQ-2026-0042", client: "Acme Corporation",     ssrId: "SSR-2026-00421", type: "Invoice Amendment",     status: "Open",        priority: "High",   assignedTo: null,               created: "2026-03-05", lastUpdated: "2026-03-05 14:30",
    messages: [{ id: "m1", sender: "John Smith",          role: "client", message: "We need to amend invoice SSR-2026-00421. The GST amount is incorrectly calculated. It should be ₹45,000 instead of ₹40,000.", timestamp: "2026-03-05 14:30", attachments: ["Original_Invoice.pdf", "GST_Calculation.xlsx"] }] },
  { id: "req-2", requestId: "REQ-2026-0041", client: "Global Tech Solutions", ssrId: "SSR-2026-00412", type: "B2C to B2B Conversion", status: "In Progress", priority: "Medium", assignedTo: "Ahmed Al-Mansoori", created: "2026-03-04", lastUpdated: "2026-03-08 11:20",
    messages: [
      { id: "m2", sender: "Sarah Chen",           role: "client", message: "We need to convert booking SSR-2026-00412 from B2C to B2B. Our corporate details: Company Name: Global Tech Solutions Inc, PAN: FGHIJ5678K, GSTIN: 27FGHIJ5678K2Y4", timestamp: "2026-03-04 09:15", attachments: ["Corporate_Details.pdf"] },
      { id: "m3", sender: "Ahmed Al-Mansoori",    role: "admin",  message: "Thank you for your request. To proceed, I need: 1. Authorized signatory name 2. Company registration certificate 3. Written authorization letter", timestamp: "2026-03-06 15:45" },
      { id: "m4", sender: "Sarah Chen",           role: "client", message: "Please find attached the required documents. Authorized Signatory: Michael Chen, CFO.", timestamp: "2026-03-08 11:20", attachments: ["Registration_Certificate.pdf", "Authorization_Letter.pdf"] },
    ]},
  { id: "req-3", requestId: "REQ-2026-0039", client: "Enterprise Dynamics",  ssrId: "SSR-2026-00398", type: "Missing Invoice",        status: "Resolved",    priority: "Low",    assignedTo: "Fatima Hassan",    created: "2026-03-01", lastUpdated: "2026-03-07 16:00",
    messages: [{ id: "m5", sender: "Michael Brown", role: "client", message: "Invoice for SSR-2026-00398 has not been received. Travel date was 2026-02-28.", timestamp: "2026-03-01 10:00" }] },
];

const teamMembers = [
  { id: "tm-1", name: "Ahmed Al-Mansoori", role: "Senior Admin" },
  { id: "tm-2", name: "Fatima Hassan",     role: "Admin" },
  { id: "tm-3", name: "Mohammed Ali",      role: "Reviewer" },
  { id: "tm-4", name: "Sarah Johnson",     role: "Manager" },
];

export default function RequestsPage() {
  const location  = useLocation();
  const isClient  = location.pathname.startsWith("/client");

  const [view, setView]           = useState("list"); // list | detail | create
  const [selected, setSelected]   = useState(null);
  const [reply, setReply]         = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState({ type: "Invoice Amendment", description: "", gstin: "", priority: "Medium" });

  const priorityColor = {
    Low:    "bg-gray-100 text-gray-700",
    Medium: "bg-yellow-100 text-yellow-700",
    High:   "bg-red-100 text-red-700",
    Urgent: "bg-red-200 text-red-900",
  };

  const openTicket = (t) => { setSelected(t); setView("detail"); };
  const backToList = () => { setView("list"); setSelected(null); setReply(""); };

  if (view === "detail" && selected) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <button onClick={backToList} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to Requests
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{selected.requestId}</h1>
                  <p className="text-gray-500 text-sm mt-1">{selected.type}</p>
                </div>
                <StatusBadge status={selected.status} type="request" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div><p className="text-gray-500">Client</p><p className="font-medium text-gray-900">{selected.client}</p></div>
                <div><p className="text-gray-500">SSR ID</p><p className="font-mono font-medium text-gray-900">{selected.ssrId}</p></div>
                <div><p className="text-gray-500">Priority</p><span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priorityColor[selected.priority]}`}>{selected.priority}</span></div>
                <div><p className="text-gray-500">Created</p><p className="font-medium text-gray-900">{selected.created}</p></div>
                <div><p className="text-gray-500">Last Updated</p><p className="font-medium text-gray-900">{selected.lastUpdated}</p></div>
                <div><p className="text-gray-500">Assigned To</p><p className="font-medium text-gray-900">{selected.assignedTo || "Unassigned"}</p></div>
              </div>
            </div>

            {/* Messages */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Conversation</h2>
              <div className="space-y-4 mb-6">
                {selected.messages.map((msg) => (
                  <div key={msg.id} className={`flex gap-3 ${msg.role === "admin" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === "admin" ? "bg-[#367588]/10" : "bg-gray-100"}`}>
                      <User className={`w-4 h-4 ${msg.role === "admin" ? "text-[#367588]" : "text-gray-600"}`} />
                    </div>
                    <div className={`flex-1 max-w-[80%] ${msg.role === "admin" ? "items-end" : ""}`}>
                      <div className={`flex items-center gap-2 mb-1 ${msg.role === "admin" ? "flex-row-reverse" : ""}`}>
                        <span className="text-xs font-semibold text-gray-900">{msg.sender}</span>
                        <span className="text-xs text-gray-400">{msg.timestamp}</span>
                      </div>
                      <div className={`p-3 rounded-lg text-sm ${msg.role === "admin" ? "bg-[#367588]/5 border border-[#367588]/20" : "bg-gray-50 border border-gray-200"}`}>
                        <p className="text-gray-800 whitespace-pre-line">{msg.message}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.attachments.map(a => (
                              <span key={a} className="text-xs bg-white border border-gray-200 px-2 py-1 rounded flex items-center gap-1">
                                📎 {a}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply box */}
              <div className="border-t border-gray-200 pt-4">
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => { if (reply.trim()) { alert("Reply sent!"); setReply(""); } }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#367588] text-white rounded-lg hover:bg-[#2a5a6b] transition-colors text-sm font-medium"
                  >
                    <Send className="w-4 h-4" /> Send Reply
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: actions */}
          {!isClient && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Assign To</h3>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm">
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name} — {m.role}</option>)}
                </select>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Update Status</h3>
                <div className="space-y-2">
                  {["Open","In Progress","Pending Client","Resolved","Closed"].map(s => (
                    <button key={s} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected.status === s ? "bg-[#367588] text-white" : "hover:bg-gray-100 text-gray-700"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Requests</h1>
          <p className="text-sm text-gray-500 mt-1">{ticketsData.length} total requests</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#367588] text-white rounded-lg hover:bg-[#2a5a6b] transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> New Request
        </button>
      </div>

      <div className="space-y-3">
        {ticketsData.map((t) => (
          <div
            key={t.id}
            onClick={() => openTicket(t)}
            className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 hover:border-[#367588] hover:shadow-sm cursor-pointer transition-all"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{t.requestId}</span>
                  <StatusBadge status={t.status} type="request" />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${priorityColor[t.priority]}`}>{t.priority}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {t.type}</span>
                  <span>{t.client}</span>
                  <span className="font-mono text-xs">{t.ssrId}</span>
                </div>
              </div>
              <div className="text-right text-xs text-gray-400">
                <p>{t.messages.length} message{t.messages.length !== 1 ? "s" : ""}</p>
                <p className="mt-0.5 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" />{t.lastUpdated}</p>
                {t.assignedTo && <p className="mt-0.5 text-[#367588]">{t.assignedTo}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">New Request</h3>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm">
                  <option>Invoice Amendment</option>
                  <option>B2C to B2B Conversion</option>
                  <option>Missing Invoice</option>
                  <option>GSTIN Mismatch</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm">
                  <option>Low</option><option>Medium</option><option>High</option><option>Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4}
                  placeholder="Describe the issue..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { alert("Request submitted!"); setShowCreate(false); }} className="flex-1 bg-[#367588] text-white py-2.5 rounded-lg font-semibold hover:bg-[#2a5a6b] transition-colors text-sm">Submit Request</button>
                <button onClick={() => setShowCreate(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
